import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Package, MapPin, Phone, Navigation, CheckCircle, Clock, AlertCircle, Menu, User, LogOut } from 'lucide-react';

import { useAuth } from '../contexts/AuthContext';
import { formatAmount } from '../utils/format';

import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';


const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5163';

const DeliveryDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [availableOrders, setAvailableOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isTracking, setIsTracking] = useState(false);
  const watchIdRef = useRef(null);
  const previousOrdersRef = useRef([]);
  const audioRef = useRef(null);

  // Crear audio de notificación
  useEffect(() => {
    console.info('🔊 Inicializando sistema de notificaciones de audio');
    
    // Crear un AudioContext para reproducir un tono
    const createNotificationSound = () => {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      const audioContext = new AudioContext();
      
      return () => {
        console.info('🎵 Reproduciendo sonido de notificación');
        
        // Resume el contexto si está suspendido
        if (audioContext.state === 'suspended') {
          audioContext.resume();
        }
        
        // Crear un tono de notificación agradable
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        oscillator.frequency.value = 800; // Frecuencia del tono
        oscillator.type = 'sine';
        
        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
        
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.5);
        
        // Segundo tono
        setTimeout(() => {
          const oscillator2 = audioContext.createOscillator();
          const gainNode2 = audioContext.createGain();
          
          oscillator2.connect(gainNode2);
          gainNode2.connect(audioContext.destination);
          
          oscillator2.frequency.value = 1000;
          oscillator2.type = 'sine';
          
          gainNode2.gain.setValueAtTime(0.3, audioContext.currentTime);
          gainNode2.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
          
          oscillator2.start(audioContext.currentTime);
          oscillator2.stop(audioContext.currentTime + 0.5);
        }, 200);
      };
    };
    
    audioRef.current = createNotificationSound();
  }, []);

  // Cargar órdenes asignadas y disponibles
  const loadOrders = async () => {
    try {
      const token = localStorage.getItem('token');
      // Solicitar órdenes con múltiples estados: pending, in_preparation, y ready
      // (limite explícito para compartir la clave de caché del backend con el resto de paneles)
      const response = await fetch(`${API_URL}/api/orders?status=pending&status=in_preparation&status=ready&limit=200`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Filtrar órdenes de delivery asignadas a este repartidor
        const myOrders = data.filter(o => 
          o.order_type === 'delivery' && 
          o.delivery_person_id === user.id &&
          o.status !== 'delivered' &&
          o.status !== 'cancelled'
        );
        setOrders(myOrders);
        
        // Filtrar órdenes de delivery disponibles (sin repartidor asignado, status ready)
        const available = data.filter(o => 
          o.order_type === 'delivery' && 
          !o.delivery_person_id &&
          o.status === 'ready'
        );
        
        // Detectar nuevas órdenes disponibles (solo si ya cargamos antes)
        const isFirstLoad = previousOrdersRef.current.length === 0 && available.length > 0;
        
        if (!isFirstLoad && previousOrdersRef.current.length >= 0) {
          const previousIds = previousOrdersRef.current.map(o => o.id);
          const newOrders = available.filter(o => !previousIds.includes(o.id));
          
          if (newOrders.length > 0) {
            console.info('🚚 Nueva orden detectada:', newOrders);
            
            // Reproducir sonido de notificación
            if (audioRef.current) {
              try {
                audioRef.current();
              } catch (error) {
                console.error('Error playing notification sound:', error);
              }
            }
            
            // Mostrar notificación visual
            toast.success(
              `🚚 ${newOrders.length} nueva(s) orden(es) de delivery disponible(s)!`,
              {
                duration: 5000,
                style: {
                  background: '#10b981',
                  color: 'white',
                  fontSize: '16px',
                  fontWeight: 'bold'
                }
              }
            );
            
            // Mostrar notificación del navegador si está permitido
            if ('Notification' in window && Notification.permission === 'granted') {
              new Notification('Nueva Orden de Delivery', {
                body: `Hay ${newOrders.length} nueva(s) orden(es) disponible(s)`,
                icon: '/favicon.ico',
                tag: 'delivery-order',
                requireInteraction: true
              });
            }
          }
        }
        
        // Actualizar referencia de órdenes previas
        previousOrdersRef.current = available;
        setAvailableOrders(available);
      }
    } catch (error) {
      console.error('Error loading orders:', error);
      toast.error('Error al cargar órdenes');
    } finally {
      setLoading(false);
    }
  };

  // Aceptar orden de delivery
  const acceptOrder = async (orderId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/mobile/orders/${orderId}/accept-delivery`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        toast.success('Orden aceptada exitosamente');
        loadOrders();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al aceptar orden');
      }
    } catch (error) {
      console.error('Error accepting order:', error);
      toast.error('Error al aceptar orden');
    }
  };

  useEffect(() => {
    loadOrders();
    
    // Solicitar permisos de notificación al cargar
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().then(permission => {
        if (permission === 'granted') {
          toast.success('Notificaciones activadas');
        }
      });
    }
    
    const interval = setInterval(loadOrders, 10000); // Refresh cada 10 segundos
    return () => clearInterval(interval);
  }, [user]);

  // Iniciar seguimiento GPS
  const startTracking = (order) => {
    if (!navigator.geolocation) {
      toast.error('Tu dispositivo no soporta geolocalización');
      return;
    }

    setSelectedOrder(order);
    setIsTracking(true);

    const watchId = navigator.geolocation.watchPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        
        // Enviar ubicación al backend
        try {
          const token = localStorage.getItem('token');
          const response = await fetch(
            `${API_URL}/api/mobile/orders/${order.id}/update-location`,
            {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ latitude, longitude })
            }
          );

          if (response.ok) {
            console.info('Ubicación actualizada:', { latitude, longitude });
          }
        } catch (error) {
          console.error('Error updating location:', error);
        }
      },
      (error) => {
        console.error('Geolocation error:', error);
        let errorMessage = 'Error obteniendo ubicación';
        
        switch(error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Permiso de ubicación denegado. Por favor, permite el acceso a tu ubicación en la configuración del navegador.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Ubicación no disponible. Verifica que el GPS esté activado.';
            break;
          case error.TIMEOUT:
            errorMessage = 'Tiempo de espera agotado al obtener ubicación.';
            break;
        }
        
        toast.error(errorMessage, { duration: 5000 });
      },
      {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 0
      }
    );

    watchIdRef.current = watchId;
    toast.success('Seguimiento GPS activado');
  };

  // Detener seguimiento GPS
  const stopTracking = () => {
    if (watchIdRef.current) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    setIsTracking(false);
    setSelectedOrder(null);
    toast.info('Seguimiento GPS detenido');
  };

  // Actualizar estado de entrega
  const updateDeliveryStatus = async (orderId, status) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/mobile/orders/${orderId}/update-delivery-status`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ delivery_status: status })
        }
      );

      if (response.ok) {
        // toast.success(`Estado actualizado: ${status}`);
        loadOrders();
        
        // Si se marca como entregado, detener tracking
        if (status === 'delivered') {
          stopTracking();
        }
      } else {
        toast.error('Error actualizando estado');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error actualizando estado');
    }
  };

  const getStatusBadge = (order) => {
    const status = order.delivery_status || 'assigned';
    const colors = {
      assigned: 'bg-gray-100 text-gray-800',
      on_the_way: 'bg-blue-100 text-blue-800',
      nearby: 'bg-orange-100 text-orange-800',
      arrived: 'bg-purple-100 text-purple-800',
      delivered: 'bg-green-100 text-green-800'
    };
    
    const labels = {
      assigned: 'Asignado',
      on_the_way: 'En Camino',
      nearby: 'Cerca',
      arrived: 'Llegué',
      delivered: 'Entregado'
    };
    
    return (
      <Badge className={colors[status] || colors.assigned}>
        {labels[status] || status}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b sticky top-0 z-50">
        <div className="max-w-full mx-auto px-3 sm:px-4 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-3">
              {/* Mobile Menu */}
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="outline" size="sm" className="md:hidden p-2">
                    <Menu className="w-5 h-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-[280px] sm:w-[350px]">
                  <SheetHeader>
                    <SheetTitle>Menú Delivery</SheetTitle>
                  </SheetHeader>
                  <div className="mt-6 space-y-4">
                    <div className="pb-4 border-b">
                      <p className="text-sm text-gray-500">Repartidor</p>
                      <p className="font-semibold">{user?.name}</p>
                    </div>
                    
                    <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                      <p className="text-xs text-gray-600 mb-1">Órdenes Disponibles</p>
                      <p className="text-2xl font-bold text-blue-600">{availableOrders.length}</p>
                    </div>
                    
                    <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                      <p className="text-xs text-gray-600 mb-1">Mis Entregas</p>
                      <p className="text-2xl font-bold text-green-600">{orders.length}</p>
                    </div>
                    
                    <Button
                      variant="outline"
                      onClick={() => navigate('/profile')}
                      className="w-full justify-start"
                    >
                      <User className="w-4 h-4 mr-2" />
                      Mi Perfil
                    </Button>
                    
                    <Button
                      variant="destructive"
                      onClick={logout}
                      className="w-full justify-start"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Cerrar Sesión
                    </Button>
                  </div>
                </SheetContent>
              </Sheet>
              
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center flex-shrink-0">
                <Package className="w-4 h-4 sm:w-6 sm:h-6 text-white" />
              </div>
              <div>
                <h1 className="text-base sm:text-xl font-bold">Mis Entregas</h1>
                <p className="text-xs sm:text-sm text-gray-600 hidden sm:block">Hola, {user?.name}</p>
              </div>
            </div>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="hidden md:flex">
                  <User className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">{user?.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>Mi Cuenta</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/profile')}>
                  <User className="w-4 h-4 mr-2" />
                  Perfil
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout}>
                  <LogOut className="w-4 h-4 mr-2" />
                  Salir
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      <div className="max-w-full mx-auto px-3 sm:px-4 py-4 sm:py-6">
        {/* Estado de Tracking Activo */}
        {isTracking && selectedOrder && (
          <Card className="mb-4 p-4 bg-blue-50 border-blue-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="bg-blue-600 text-white rounded-full p-2 animate-pulse">
                  <Navigation className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-semibold text-blue-900">
                    Seguimiento GPS Activo
                  </p>
                  <p className="text-sm text-blue-700">
                    Orden #{selectedOrder.order_number}
                  </p>
                </div>
              </div>
              <Button variant="outline" onClick={stopTracking} size="sm">
                Detener
              </Button>
            </div>
          </Card>
        )}

        {/* Órdenes Disponibles */}
        {availableOrders.length > 0 && (
          <div className="mb-6">
            <h2 className="text-lg font-semibold mb-3 text-orange-600 flex items-center gap-2">
              <Package className="w-5 h-5" />
              Órdenes Disponibles ({availableOrders.length})
            </h2>
            <div className="space-y-3">
              {availableOrders.map((order) => (
                <Card key={order.id} className="p-4 bg-orange-50 border-orange-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-bold">Orden #{order.order_number}</h3>
                      <p className="text-sm text-gray-600 mt-1">
                        <MapPin className="w-4 h-4 inline mr-1" />
                        {order.delivery_address || 'Sin dirección'}
                      </p>
                      <p className="text-sm text-gray-500 mt-1">
                        Total: ${formatAmount(order.total)}
                      </p>
                    </div>
                    <Button 
                      onClick={() => acceptOrder(order.id)}
                      className="bg-orange-500 hover:bg-orange-600 text-white"
                    >
                      Aceptar
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Lista de Órdenes Asignadas */}
        <h2 className="text-lg font-semibold mb-3">Mis Entregas</h2>
        {orders.length === 0 ? (
          <Card className="p-8 text-center">
            <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 text-lg">No tienes entregas asignadas</p>
            <p className="text-gray-500 text-sm mt-2">
              Acepta órdenes disponibles arriba
            </p>
          </Card>
        ) : (
          <div className="space-y-4">
            {orders.map((order) => (
              <Card key={order.id} className="p-6">
                <div className="space-y-4">
                  {/* Header */}
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-bold text-lg">Orden #{order.order_number}</h3>
                      <p className="text-sm text-gray-600 mt-1">
                        <Clock className="w-4 h-4 inline mr-1" />
                        {new Date(order.created_at).toLocaleString('es-ES', {
                          day: '2-digit',
                          month: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                    {getStatusBadge(order)}
                  </div>

                  {/* Cliente */}
                  <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                    <p className="font-semibold text-sm text-gray-700">Cliente</p>
                    <p className="font-medium">{order.customer_name}</p>
                    {order.customer_phone && (
                      <a 
                        href={`tel:${order.customer_phone}`}
                        className="flex items-center gap-2 text-blue-600 hover:text-blue-700 text-sm"
                      >
                        <Phone className="w-4 h-4" />
                        {order.customer_phone}
                      </a>
                    )}
                  </div>

                  {/* Dirección */}
                  {order.delivery_address && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="font-semibold text-sm text-gray-700 mb-2">
                        <MapPin className="w-4 h-4 inline mr-1" />
                        Dirección de Entrega
                      </p>
                      <p className="text-gray-900">{order.delivery_address}</p>
                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(order.delivery_address)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-700 text-sm mt-2 inline-block"
                      >
                        Abrir en Google Maps →
                      </a>
                    </div>
                  )}

                  {/* Items */}
                  <div className="border-t pt-4">
                    <p className="font-semibold text-sm text-gray-700 mb-3">Productos</p>
                    <div className="space-y-2">
                      {order.items.map((item, idx) => (
                        <div key={idx} className="flex justify-between text-sm">
                          <span className="text-gray-700">
                            {item.quantity}x {item.product_name}
                            {item.notes && (
                              <span className="text-xs text-orange-600 block">
                                📝 {item.notes}
                              </span>
                            )}
                          </span>
                          <span className="font-medium">${formatAmount(item.subtotal)}</span>
                        </div>
                      ))}
                    </div>
                    <div className="border-t mt-3 pt-3">
                      <div className="flex justify-between font-bold">
                        <span>Total</span>
                        <span>${formatAmount(order.total)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Acciones */}
                  <div className="border-t pt-4 space-y-2">
                    {!order.delivery_status || order.delivery_status === 'assigned' ? (
                      <>
                        <Button 
                          className="w-full"
                          onClick={() => {
                            startTracking(order);
                            updateDeliveryStatus(order.id, 'on_the_way');
                          }}
                          disabled={isTracking}
                        >
                          <Navigation className="w-4 h-4 mr-2" />
                          Iniciar Entrega
                        </Button>
                      </>
                    ) : order.delivery_status === 'on_the_way' ? (
                      <>
                        {!isTracking && (
                          <Button 
                            className="w-full bg-blue-600"
                            onClick={() => startTracking(order)}
                          >
                            <Navigation className="w-4 h-4 mr-2" />
                            Reanudar Seguimiento GPS
                          </Button>
                        )}
                        <Button 
                          className="w-full"
                          variant="outline"
                          onClick={() => updateDeliveryStatus(order.id, 'nearby')}
                        >
                          <AlertCircle className="w-4 h-4 mr-2" />
                          Estoy Cerca
                        </Button>
                      </>
                    ) : order.delivery_status === 'nearby' ? (
                      <Button 
                        className="w-full"
                        onClick={() => updateDeliveryStatus(order.id, 'arrived')}
                      >
                        <MapPin className="w-4 h-4 mr-2" />
                        He Llegado
                      </Button>
                    ) : order.delivery_status === 'arrived' ? (
                      <Button 
                        className="w-full bg-green-600"
                        onClick={() => updateDeliveryStatus(order.id, 'delivered')}
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Marcar como Entregado
                      </Button>
                    ) : null}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DeliveryDashboard;
